import React, { useState, useEffect } from 'react';
import { X, Eye, EyeOff, LogIn, UserPlus, CheckCircle, AlertCircle } from 'lucide-react';
import GoogleSignInButton from './GoogleSignInButton';
import TermsOfService from './TermsOfService';

const SignInOverlay = ({ onClose, onSignIn, onRegister, onForgotPassword, initialTab }) => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [activeTab, setActiveTab] = useState(initialTab);
    const [agreedToTerms, setAgreedToTerms] = useState(false);
    const [passwordResetStatus, setPasswordResetStatus] = useState(null);
    const [resetErrorMessage, setResetErrorMessage] = useState('');
    const [isNewGoogleUser, setIsNewGoogleUser] = useState(false);
    const [googleUserData, setGoogleUserData] = useState(null);

    const handleSubmit = (e) => {
        e.preventDefault();
        if (activeTab === 'signin') {
            onSignIn('email', { email, password });
        } else if (activeTab === 'register' && agreedToTerms) {
            onRegister({ email, password });
        }
    };

    const isValidEmail = (email) => {
        // Basic email validation regex
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    };

    const handleEmailChange = (e) => {
        const newEmail = e.target.value;
        setEmail(newEmail);
        if (isValidEmail(newEmail) && passwordResetStatus === 'error') {
            setPasswordResetStatus(null);
            setResetErrorMessage('');
        }
    };

    const handleForgotPassword = async () => {
        if (!isValidEmail(email)) {
            setPasswordResetStatus('error');
            setResetErrorMessage('Please enter a valid email address.');
            return;
        }

        try {
            await onForgotPassword(email);
            setPasswordResetStatus('sent');
        } catch (error) {
            setPasswordResetStatus('error');
            setResetErrorMessage('Failed to send reset email. Please try again.');
        }
    };

    const handleGoogleSignIn = async () => {
        try {
          const result = await onSignIn('google');
          if (result && result.user) {
            if (result.additionalUserInfo && result.additionalUserInfo.isNewUser) {
              setIsNewGoogleUser(true);
              setGoogleUserData(result.user);
            } else {
              // Existing user, proceed with sign in
              // No need to call onSignIn again, as it was already called
            }
          } else {
            console.error('Google sign-in failed: No user data returned');
          }
        } catch (error) {
          console.error('Google sign-in error:', error);
        }
      };

    const handleGoogleUserTermsAgreement = () => {
        if (agreedToTerms) {
            onRegister(googleUserData);
            setIsNewGoogleUser(false);
            setGoogleUserData(null);
        }
    };

    useEffect(() => {
        let timer;
        if (passwordResetStatus === 'sent') {
            timer = setTimeout(() => {
                setPasswordResetStatus(null);
                setResetErrorMessage('');
            }, 5000);
        }
        return () => clearTimeout(timer);
    }, [passwordResetStatus]);

    const gradientStyle = "bg-gradient-to-r from-blue-500 to-purple-600 text-white";
    const tabStyle = (isActive) => `flex-1 flex items-center justify-center px-4 py-2 ${isActive ? gradientStyle : 'bg-gray-200 text-gray-700'} transition-colors duration-200`;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white text-gray-800 rounded-lg max-w-md w-full overflow-hidden">
                {!isNewGoogleUser ? (
                    <>
                        <div className="flex">
                            <button
                                className={tabStyle(activeTab === 'signin')}
                                onClick={() => setActiveTab('signin')}
                            >
                                <LogIn size={20} className="mr-2" />
                                Sign in
                            </button>
                            <button
                                className={tabStyle(activeTab === 'register')}
                                onClick={() => setActiveTab('register')}
                            >
                                <UserPlus size={20} className="mr-2" />
                                Register
                            </button>
                        </div>

                        <div className="p-6">
                            <div className="flex justify-between items-center mb-4">
                                <h2 className="text-xl font-bold">Welcome to AisenHour</h2>
                                <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
                                    <X size={24} />
                                </button>
                            </div>

                            <form onSubmit={handleSubmit} className="space-y-4">
                                {activeTab === 'register' && (
                                    <>
                                        <TermsOfService />
                                        <div className="flex items-center">
                                            <input
                                                type="checkbox"
                                                id="terms"
                                                checked={agreedToTerms}
                                                onChange={(e) => setAgreedToTerms(e.target.checked)}
                                                className="mr-2"
                                                required
                                            />
                                            <label htmlFor="terms" className="text-sm">
                                                I agree to the Terms of Service
                                            </label>
                                        </div>
                                    </>
                                )}
                                <GoogleSignInButton
                                    onClick={handleGoogleSignIn}
                                    disabled={activeTab === 'register' && !agreedToTerms}
                                />
                        <div className="text-center text-gray-500 my-4">Or continue with</div>
                        <input
                            type="email"
                            placeholder="Email address"
                            value={email}
                            onChange={handleEmailChange}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                            required
                        />
                        <div className="relative">
                            <input
                                type={showPassword ? "text" : "password"}
                                placeholder="Password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg pr-10"
                                required
                            />
                            <button
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500"
                            >
                                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                            </button>
                        </div>
                        {activeTab === 'signin' && (
                            <div className="text-right">
                                {passwordResetStatus === 'sent' ? (
                                    <div className="flex items-center justify-end text-green-600">
                                        <CheckCircle size={16} className="mr-1" />
                                        <span className="text-sm">Check your email for the recovery link</span>
                                    </div>
                                ) : passwordResetStatus === 'error' ? (
                                    <div className="flex items-center justify-end text-red-600">
                                        <AlertCircle size={16} className="mr-1" />
                                        <span className="text-sm">{resetErrorMessage}</span>
                                    </div>
                                ) : (
                                    <button
                                        type="button"
                                        onClick={handleForgotPassword}
                                        className="text-blue-600 hover:underline text-sm"
                                    >
                                        Forgot your password?
                                    </button>
                                )}
                            </div>
                        )}
                        <button
                            type="submit"
                            className={`w-full ${gradientStyle} px-4 py-2 rounded-lg font-semibold hover:opacity-90 transition-colors ${(activeTab === 'register' && !agreedToTerms) ? 'opacity-50 cursor-not-allowed' : ''
                                }`}
                            disabled={activeTab === 'register' && !agreedToTerms}
                        >
                            {activeTab === 'signin' ? 'Sign in' : 'Register'}
                        </button>
                        </form>
                        </div>
                    </>
                ) : (
                    <div className="p-6">
                        <h2 className="text-xl font-bold mb-4">Welcome to AisenHour</h2>
                        <p className="mb-4">To complete your registration with Google, please review and agree to our Terms of Service:</p>
                        <TermsOfService />
                        <div className="flex items-center mt-4">
                            <input
                                type="checkbox"
                                id="google-terms"
                                checked={agreedToTerms}
                                onChange={(e) => setAgreedToTerms(e.target.checked)}
                                className="mr-2"
                                required
                            />
                            <label htmlFor="google-terms" className="text-sm">
                                I agree to the Terms of Service
                            </label>
                        </div>
                        <button
                            onClick={handleGoogleUserTermsAgreement}
                            className={`w-full ${gradientStyle} px-4 py-2 rounded-lg font-semibold hover:opacity-90 transition-colors mt-4 ${!agreedToTerms ? 'opacity-50 cursor-not-allowed' : ''}`}
                            disabled={!agreedToTerms}
                        >
                            Complete Registration
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default SignInOverlay;